<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  initialServices: {
    type: Array,
    default: () => [],
  },
  professionals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);
const { isAdmin } = useAdmin();

const services = ref(
  props.initialServices && props.initialServices.length
    ? [...props.initialServices]
    : []
);
const localProfessionals = ref(
  props.professionals && props.professionals.length
    ? [...props.professionals]
    : []
);

const isLoading = ref(false);
const showModal = ref(false);
const isSaving = ref(false);
const editingService = ref(null);

watch(
  () => props.initialServices,
  newVal => {
    if (newVal && newVal.length && !services.value.length) {
      services.value = [...newVal];
    }
  }
);

watch(
  () => props.professionals,
  newVal => {
    if (newVal && newVal.length) {
      localProfessionals.value = [...newVal];
    }
  }
);

// Modal de Aviso de Inconsistência de Escala
const showConflictModal = ref(false);
const conflictWarningText = ref('');

const colorPresets = [
  '#F59E0B', // Amber / Laranja
  '#3B82F6', // Blue
  '#10B981', // Emerald / Verde
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#EF4444', // Red
  '#06B6D4', // Cyan
  '#64748B', // Slate
];

const weekDaysList = [
  { key: 'monday', label: 'Seg', full: 'Segunda-feira' },
  { key: 'tuesday', label: 'Ter', full: 'Terça-feira' },
  { key: 'wednesday', label: 'Qua', full: 'Quarta-feira' },
  { key: 'thursday', label: 'Qui', full: 'Quinta-feira' },
  { key: 'friday', label: 'Sex', full: 'Sexta-feira' },
  { key: 'saturday', label: 'Sáb', full: 'Sábado' },
];

const allTimeSlots = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
  '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
  '18:00', '19:00', '20:00', '21:00', '22:00',
];

const morningSlots = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
const afternoonSlots = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const nightSlots = ['18:00', '19:00', '20:00', '21:00', '22:00'];

const formData = ref({
  id: null,
  name: '',
  description: '',
  attendance_type: 'individual',
  billing_type: 'package',
  duration_minutes: 60,
  price: 0,
  color: '#3B82F6',
  status: 'active',
  max_concurrent_rooms: 1,
  allow_trial_class: false,
  custom_schedule_enabled: false,
  allowed_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  allowed_time_slots: [...morningSlots, ...afternoonSlots],
});

const fetchSharedData = async () => {
  if (services.value.length === 0) {
    isLoading.value = true;
  }
  try {
    const [srvRes, profRes] = await Promise.all([
      schedulingAPI.getServices(),
      schedulingAPI.getProfessionals(),
    ]);
    services.value = srvRes.data || [];
    localProfessionals.value = profRes.data || [];
  } catch (err) {
    console.error('Erro ao carregar dados:', err);
  } finally {
    isLoading.value = false;
  }
};

const openNewService = () => {
  editingService.value = null;
  formData.value = {
    id: null,
    name: '',
    description: '',
    attendance_type: 'individual',
    billing_type: 'package',
    duration_minutes: 60,
    price: 0,
    color: '#3B82F6',
    status: 'active',
    max_concurrent_rooms: 1,
    allow_trial_class: false,
    custom_schedule_enabled: false,
    allowed_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    allowed_time_slots: [...morningSlots, ...afternoonSlots],
  };
  showModal.value = true;
};

const openEditService = s => {
  editingService.value = s;
  formData.value = {
    id: s.id,
    name: s.name || '',
    description: s.description || '',
    attendance_type: s.attendance_type || 'individual',
    billing_type: s.billing_type || 'package',
    duration_minutes: s.duration_minutes || 60,
    price: s.price || 0,
    color: s.color || '#3B82F6',
    status: s.status || 'active',
    max_concurrent_rooms: s.max_concurrent_rooms || 1,
    allow_trial_class: !!s.allow_trial_class,
    custom_schedule_enabled: !!s.custom_schedule_enabled,
    allowed_days: s.allowed_days?.length ? [...s.allowed_days] : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    allowed_time_slots: s.allowed_time_slots?.length ? [...s.allowed_time_slots] : [...morningSlots, ...afternoonSlots],
  };
  showModal.value = true;
};

const toggleDay = dayKey => {
  const idx = formData.value.allowed_days.indexOf(dayKey);
  if (idx > -1) {
    formData.value.allowed_days.splice(idx, 1);
  } else {
    formData.value.allowed_days.push(dayKey);
  }
};

const toggleSlot = slot => {
  const idx = formData.value.allowed_time_slots.indexOf(slot);
  if (idx > -1) {
    formData.value.allowed_time_slots.splice(idx, 1);
  } else {
    formData.value.allowed_time_slots.push(slot);
  }
};

const selectShift = shift => {
  let target = [];
  if (shift === 'morning') target = morningSlots;
  if (shift === 'afternoon') target = afternoonSlots;
  if (shift === 'night') target = nightSlots;
  if (shift === 'all') target = allTimeSlots;

  const allSelected = target.every(s => formData.value.allowed_time_slots.includes(s));
  if (allSelected) {
    formData.value.allowed_time_slots = formData.value.allowed_time_slots.filter(s => !target.includes(s));
  } else {
    formData.value.allowed_time_slots = Array.from(new Set([...formData.value.allowed_time_slots, ...target]));
  }
};

const validateProfessionalAvailability = () => {
  if (!formData.value.custom_schedule_enabled) return true;
  if (formData.value.allowed_days.length === 0 || formData.value.allowed_time_slots.length === 0) return true;
  if (localProfessionals.value.length === 0) {
    conflictWarningText.value = 'Você ainda não possui nenhum profissional cadastrado para atender neste serviço e horários configurados.';
    return false;
  }

  const serviceId = formData.value.id;
  const eligibleProfessionals = localProfessionals.value.filter(p => {
    if (!p.service_ids || p.service_ids.length === 0) return true;
    if (serviceId) return p.service_ids.includes(serviceId);
    return true;
  });

  if (eligibleProfessionals.length === 0) {
    conflictWarningText.value = 'Nenhum profissional cadastrado está habilitado para realizar este serviço.';
    return false;
  }

  return true;
};

const handleSaveClick = async (force = false) => {
  if (!formData.value.name.trim()) {
    alert('Por favor, informe o nome do serviço.');
    return;
  }

  if (formData.value.custom_schedule_enabled) {
    if (formData.value.allowed_days.length === 0) {
      alert('Selecione pelo menos um dia da semana para o atendimento deste serviço.');
      return;
    }
    if (formData.value.allowed_time_slots.length === 0) {
      alert('Selecione pelo menos um horário de atendimento para este serviço.');
      return;
    }

    if (!force && !validateProfessionalAvailability()) {
      showConflictModal.value = true;
      return;
    }
  }

  showConflictModal.value = false;
  isSaving.value = true;
  try {
    if (formData.value.id) {
      await schedulingAPI.updateService(formData.value.id, formData.value);
    } else {
      await schedulingAPI.createService(formData.value);
    }
    showModal.value = false;
    await fetchSharedData();
    emit('saved');
  } catch (err) {
    alert('Erro ao salvar serviço.');
  } finally {
    isSaving.value = false;
  }
};

const deleteService = async s => {
  if (!confirm(`Tem certeza que deseja excluir o serviço "${s.name}"?`)) return;

  try {
    await schedulingAPI.deleteService(s.id);
    await fetchSharedData();
    emit('saved');
  } catch (err) {
    alert('Não é possível excluir um serviço com agendamentos vinculados.');
  }
};

const formatPrice = val => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(val) || 0);
};

const copyServiceLink = s => {
  const url = `${window.location.origin}/schedule?service_id=${s.id}`;
  navigator.clipboard.writeText(url);
  alert(`Link do serviço "${s.name}" copiado para a área de transferência!`);
};

onMounted(() => {
  if (services.value.length === 0) {
    fetchSharedData();
  }
});
</script>

<template>
  <div class="space-y-5 select-none">
    <!-- Action Bar -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-n-slate-12">Serviços & Procedimentos</h2>
        <p class="text-xs text-n-slate-11">
          Gerencie catálogo de procedimentos, durações, preços e regras exclusivas de horários.
        </p>
      </div>

      <button
        v-if="isAdmin"
        type="button"
        class="h-8 px-3 rounded-lg text-xs font-medium text-white bg-brand hover:bg-brand-hover active:bg-brand transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
        @click="openNewService"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>Novo Serviço</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="services.length === 0"
      class="p-12 text-center border border-dashed border-n-weak rounded-xl bg-n-surface-1"
    >
      <div class="w-12 h-12 rounded-xl bg-n-alpha-2 text-n-brand flex items-center justify-center mx-auto mb-3">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Nenhum serviço cadastrado</h3>
      <p class="text-xs text-n-slate-11 max-w-sm mx-auto mb-4">
        Cadastre os serviços oferecidos (ex: Avaliação, Pilates, Fisioterapia) para permitir agendamentos e regras de horários.
      </p>
      <button
        v-if="isAdmin"
        type="button"
        class="h-8 px-3 rounded-lg text-xs font-medium text-white bg-brand hover:bg-brand-hover transition-all cursor-pointer shadow-xs inline-flex items-center gap-1.5"
        @click="openNewService"
      >
        <span>Cadastrar Primeiro Serviço</span>
      </button>
    </div>

    <!-- Services Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
      <div
        v-for="s in services"
        :key="s.id"
        class="p-4 rounded-xl bg-n-surface-1 border border-n-weak hover:border-n-weak/80 transition-all shadow-xs flex flex-col justify-between relative overflow-hidden group"
      >
        <!-- Color Header Line -->
        <div
          class="absolute top-0 left-0 right-0 h-1"
          :style="{ backgroundColor: s.color || '#3B82F6' }"
        />

        <div>
          <!-- Header: Title & Actions -->
          <div class="flex items-start justify-between gap-2 mb-2">
            <h3 class="text-sm font-semibold text-n-slate-12 group-hover:text-n-brand transition-colors">
              {{ s.name }}
            </h3>

            <div v-if="isAdmin" class="flex items-center gap-1">
              <button
                type="button"
                class="p-1 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors cursor-pointer"
                title="Editar Serviço"
                @click="openEditService(s)"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Duration, Price & Schedule Details -->
          <div class="flex items-center gap-3.5 text-xs text-n-slate-11 font-medium mb-3">
            <span class="flex items-center gap-1">
              <svg class="w-3.5 h-3.5 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ s.duration_minutes }} min
            </span>

            <span class="font-semibold text-n-slate-12 font-mono">
              {{ formatPrice(s.price) }}
            </span>
          </div>

          <!-- Modalidade, Custom Schedule Tag & Rooms Tag -->
          <div class="mb-3 flex flex-wrap items-center gap-1.5">
            <!-- Modalidade de Cobrança -->
            <span
              v-if="s.billing_type === 'recurring'"
              class="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 font-semibold inline-flex items-center gap-1"
            >
              <span>🧘‍♀️</span>
              <span>Mensalidade</span>
            </span>
            <span
              v-else-if="s.billing_type === 'single'"
              class="text-[10px] px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 border border-n-weak font-semibold inline-flex items-center gap-1"
            >
              <span>⚡</span>
              <span>Sessão Avulsa</span>
            </span>
            <span
              v-else
              class="text-[10px] px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 font-semibold inline-flex items-center gap-1"
            >
              <span>🩺</span>
              <span>Pacote</span>
            </span>

            <span
              v-if="s.attendance_type === 'group'"
              class="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-semibold inline-flex items-center gap-1"
            >
              <span>👥</span>
              <span>Grupo</span>
            </span>
            <span
              v-else
              class="text-[10px] px-2 py-0.5 rounded-md bg-brand-subtle text-n-brand border border-brand/20 font-semibold inline-flex items-center gap-1"
            >
              <span>👤</span>
              <span>Individual</span>
            </span>

            <span
              v-if="s.custom_schedule_enabled"
              class="text-[10px] px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 font-semibold inline-flex items-center gap-1"
            >
              <span>⏰</span>
              <span>{{ s.allowed_days?.length || 0 }} dias</span>
            </span>

            <span
              v-if="s.max_concurrent_rooms > 1"
              class="text-[10px] px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 border border-n-weak font-semibold inline-flex items-center gap-1"
            >
              <span>🏢</span>
              <span>{{ s.max_concurrent_rooms }} Salas</span>
            </span>

            <span
              v-if="s.allow_trial_class"
              class="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 font-semibold inline-flex items-center gap-1"
            >
              <span>⭐</span>
              <span>Aula Exp.</span>
            </span>
          </div>

          <p v-if="s.description" class="text-xs text-n-slate-11 line-clamp-2 mb-3">
            {{ s.description }}
          </p>
        </div>

        <!-- Footer Card: Excluir Link & Copy Link -->
        <div class="pt-2.5 border-t border-n-weak flex items-center justify-between">
          <button
            v-if="isAdmin"
            type="button"
            class="text-xs font-medium text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors cursor-pointer"
            @click="deleteService(s)"
          >
            Excluir
          </button>
          <span v-else class="text-xs text-n-slate-10">Catálogo</span>

          <button
            type="button"
            class="text-xs font-medium text-n-brand hover:underline flex items-center gap-1 cursor-pointer"
            title="Copiar link direto para este serviço"
            @click="copyServiceLink(s)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>Copiar Link</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Serviço -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in"
      @click.self="showModal = false"
    >
      <div
        class="w-full max-w-lg bg-n-surface-1 border border-n-weak rounded-2xl shadow-2xl overflow-hidden text-n-slate-12 max-h-[92vh] flex flex-col"
      >
        <!-- Modal Header -->
        <div class="px-5 py-3.5 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ formData.id ? 'Editar Serviço' : 'Novo Serviço de Atendimento' }}
          </h3>
          <button
            type="button"
            class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer transition-colors"
            @click="showModal = false"
          >
            ✕
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Nome do Serviço <span class="text-rose-500">*</span>
            </label>
            <input
              v-model="formData.name"
              type="text"
              placeholder="Ex: Fisioterapia Particular, Pilates Grupo, Avaliação..."
              class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none transition-colors"
            />
          </div>

          <!-- Tipo de Atendimento: Individual vs Grupo -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              Modalidade de Atendimento <span class="text-rose-500">*</span>
            </label>
            <div class="grid grid-cols-2 gap-2">
              <button
                type="button"
                class="p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-0.5"
                :class="
                  formData.attendance_type === 'individual'
                    ? 'bg-brand-subtle border-brand text-n-slate-12 ring-1 ring-brand'
                    : 'bg-n-alpha-1 dark:bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-weak/80'
                "
                @click="formData.attendance_type = 'individual'"
              >
                <div class="flex items-center gap-1.5 font-semibold text-xs" :class="formData.attendance_type === 'individual' ? 'text-n-brand' : 'text-n-slate-12'">
                  <span>👤</span>
                  <span>Particular / Individual</span>
                </div>
                <p class="text-[10px] text-n-slate-10 leading-tight">
                  1 a 1 exclusivo. Bloqueia o horário do profissional para 1 único paciente.
                </p>
              </button>

              <button
                type="button"
                class="p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-0.5"
                :class="
                  formData.attendance_type === 'group'
                    ? 'bg-emerald-500/10 border-emerald-500 text-n-slate-12 ring-1 ring-emerald-500'
                    : 'bg-n-alpha-1 dark:bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-weak/80'
                "
                @click="formData.attendance_type = 'group'"
              >
                <div class="flex items-center gap-1.5 font-semibold text-xs" :class="formData.attendance_type === 'group' ? 'text-emerald-500' : 'text-n-slate-12'">
                  <span>👥</span>
                  <span>Em Grupo / Coletivo</span>
                </div>
                <p class="text-[10px] text-n-slate-10 leading-tight">
                  Turmas compartilhadas. Permite múltiplos alunos até a capacidade máxima.
                </p>
              </button>
            </div>
          </div>

          <!-- SEÇÃO: MODALIDADE DE COBRANÇA (MENSALIDADE vs PACOTE) -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              Modalidade de Cobrança & Ciclo <span class="text-rose-500">*</span>
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                class="p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-0.5"
                :class="
                  formData.billing_type === 'recurring'
                    ? 'bg-amber-500/10 border-amber-500 text-n-slate-12 ring-1 ring-amber-500'
                    : 'bg-n-alpha-1 dark:bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-weak/80'
                "
                @click="formData.billing_type = 'recurring'"
              >
                <div class="flex items-center gap-1 font-semibold text-xs" :class="formData.billing_type === 'recurring' ? 'text-amber-500' : 'text-n-slate-12'">
                  <span>🧘‍♀️</span>
                  <span>Mensalidade</span>
                </div>
                <p class="text-[10px] text-n-slate-10 leading-tight">
                  Pilates / Assinatura contínua. Vaga fixa na grade.
                </p>
              </button>

              <button
                type="button"
                class="p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-0.5"
                :class="
                  formData.billing_type === 'package'
                    ? 'bg-cyan-500/10 border-cyan-500 text-n-slate-12 ring-1 ring-cyan-500'
                    : 'bg-n-alpha-1 dark:bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-weak/80'
                "
                @click="formData.billing_type = 'package'"
              >
                <div class="flex items-center gap-1 font-semibold text-xs" :class="formData.billing_type === 'package' ? 'text-cyan-500' : 'text-n-slate-12'">
                  <span>🩺</span>
                  <span>Pacote</span>
                </div>
                <p class="text-[10px] text-n-slate-10 leading-tight">
                  Fisioterapia / Reabilitação. Quantidade fixa de sessões.
                </p>
              </button>

              <button
                type="button"
                class="p-2.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col gap-0.5"
                :class="
                  formData.billing_type === 'single'
                    ? 'bg-brand-subtle border-brand text-n-slate-12 ring-1 ring-brand'
                    : 'bg-n-alpha-1 dark:bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-weak/80'
                "
                @click="formData.billing_type = 'single'"
              >
                <div class="flex items-center gap-1 font-semibold text-xs" :class="formData.billing_type === 'single' ? 'text-n-brand' : 'text-n-slate-12'">
                  <span>⚡</span>
                  <span>Avulso</span>
                </div>
                <p class="text-[10px] text-n-slate-10 leading-tight">
                  Consulta ou sessão avulsa pontual.
                </p>
              </button>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-2.5">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Duração (Min) <span class="text-rose-500">*</span>
              </label>
              <input
                v-model.number="formData.duration_minutes"
                type="number"
                step="5"
                min="5"
                placeholder="60"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 focus:border-brand focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Valor (R$)
              </label>
              <input
                v-model.number="formData.price"
                type="number"
                step="0.01"
                min="0"
                placeholder="250.00"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 font-mono focus:border-brand focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Salas Físicas
              </label>
              <input
                v-model.number="formData.max_concurrent_rooms"
                type="number"
                min="1"
                placeholder="1"
                class="w-full h-8 px-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 focus:border-brand focus:outline-none transition-colors"
                title="Quantidade de salas simultâneas para este serviço"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              Cor de Identificação
            </label>
            <div class="flex items-center gap-2">
              <button
                v-for="color in colorPresets"
                :key="color"
                type="button"
                class="w-5 h-5 rounded-full transition-transform cursor-pointer shrink-0"
                :style="{
                  backgroundColor: color,
                  transform: formData.color === color ? 'scale(1.2)' : 'scale(1)',
                  boxShadow: formData.color === color ? '0 0 0 2px var(--n-surface-1), 0 0 0 3.5px var(--brand-color)' : 'none'
                }"
                @click="formData.color = color"
              />
              <input
                v-model="formData.color"
                type="color"
                class="w-5 h-5 rounded-md cursor-pointer bg-transparent border-0 p-0 ml-1"
                title="Cor customizada"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Descrição do Atendimento
            </label>
            <textarea
              v-model="formData.description"
              rows="2"
              placeholder="Instruções ou resumo sobre este serviço..."
              class="w-full p-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:border-brand focus:outline-none resize-none transition-colors"
            />
          </div>

          <!-- SEÇÃO: AULA EXPERIMENTAL (OPCIONAL) -->
          <div class="p-3.5 rounded-xl bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak space-y-2">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-semibold text-n-slate-12 flex items-center gap-1.5">
                  <span>⭐</span>
                  <span>Permitir Aula Experimental</span>
                </span>
                <span class="text-[10px] text-n-slate-10 block">
                  Habilita agendamento de aula experimental para este serviço na grade
                </span>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="formData.allow_trial_class"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div class="w-8 h-4 bg-n-alpha-2 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-brand"></div>
              </label>
            </div>
          </div>

          <!-- SEÇÃO: DIAS E HORÁRIOS EXCLUSIVOS DO SERVIÇO (OPCIONAL) -->
          <div class="p-3.5 rounded-xl bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak space-y-3">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-semibold text-n-slate-12 block">Definir Dias e Horários Específicos</span>
                <span class="text-[10px] text-n-slate-10">Bloqueia agendamentos fora da escala deste serviço</span>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="formData.custom_schedule_enabled"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div class="w-8 h-4 bg-n-alpha-2 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-brand"></div>
              </label>
            </div>

            <!-- Opções quando ativado -->
            <div v-if="formData.custom_schedule_enabled" class="space-y-3 pt-2 border-t border-n-weak">
              <!-- Dias da Semana Permitidos -->
              <div>
                <label class="block text-[11px] font-medium text-n-slate-11 mb-1.5">
                  Dias da Semana Permitidos:
                </label>
                <div class="grid grid-cols-6 gap-1">
                  <button
                    v-for="d in weekDaysList"
                    :key="d.key"
                    type="button"
                    class="py-1 text-xs font-medium rounded-lg border text-center transition-all cursor-pointer"
                    :class="[
                      formData.allowed_days.includes(d.key)
                        ? 'bg-brand border-brand text-white shadow-xs font-semibold'
                        : 'bg-n-surface-1 border-n-weak text-n-slate-11 hover:text-n-slate-12'
                    ]"
                    @click="toggleDay(d.key)"
                  >
                    {{ d.label }}
                  </button>
                </div>
              </div>

              <!-- Horários Permitidos -->
              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label class="text-[11px] font-medium text-n-slate-11">
                    Horários Permitidos:
                  </label>
                  <!-- Atalhos de Turnos -->
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
                      @click="selectShift('morning')"
                    >
                      Manhã
                    </button>
                    <button
                      type="button"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
                      @click="selectShift('afternoon')"
                    >
                      Tarde
                    </button>
                    <button
                      type="button"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
                      @click="selectShift('night')"
                    >
                      Noite
                    </button>
                    <button
                      type="button"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
                      @click="selectShift('all')"
                    >
                      Todos
                    </button>
                  </div>
                </div>

                <!-- Grid de Chips de Horários -->
                <div class="grid grid-cols-6 gap-1 max-h-32 overflow-y-auto p-1 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-xl">
                  <button
                    v-for="slot in allTimeSlots"
                    :key="slot"
                    type="button"
                    class="py-1 text-[11px] font-medium rounded-md border text-center transition-all cursor-pointer"
                    :class="[
                      formData.allowed_time_slots.includes(slot)
                        ? 'bg-brand border-brand text-white shadow-xs font-semibold'
                        : 'bg-n-surface-1 border-n-weak text-n-slate-10 hover:text-n-slate-12'
                    ]"
                    @click="toggleSlot(slot)"
                  >
                    {{ slot }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="px-5 py-3 border-t border-n-weak bg-n-alpha-1 flex items-center justify-end gap-2">
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors cursor-pointer"
            @click="showModal = false"
          >
            Cancelar
          </button>

          <button
            type="button"
            class="h-8 px-4 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg shadow-xs transition-all cursor-pointer disabled:opacity-50"
            :disabled="isSaving"
            @click="handleSaveClick(false)"
          >
            {{ isSaving ? 'Salvando...' : 'Salvar Serviço' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Aviso: Inconsistência de Escala de Profissionais -->
    <div
      v-if="showConflictModal"
      class="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in"
    >
      <div class="w-full max-w-md bg-n-surface-1 border border-amber-500/40 rounded-2xl shadow-2xl p-5 text-n-slate-12 space-y-3.5">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-500 flex items-center justify-center font-bold text-base shrink-0">
            ⚠️
          </div>
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">Inconsistência de Escala</h3>
            <p class="text-xs text-amber-500 font-medium">Nenhum profissional disponível no horário</p>
          </div>
        </div>

        <p class="text-xs text-n-slate-11 leading-relaxed bg-n-alpha-1 p-3 rounded-xl border border-n-weak">
          {{ conflictWarningText || 'Nenhum profissional cadastrado possui escala compatível para atender neste serviço nos dias e horários selecionados.' }}
        </p>

        <p class="text-xs text-n-slate-10">
          Deseja salvar esta configuração mesmo assim? (Você poderá ajustar os profissionais depois).
        </p>

        <div class="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg cursor-pointer transition-colors"
            @click="showConflictModal = false"
          >
            Voltar e Ajustar
          </button>

          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg shadow-xs cursor-pointer transition-all"
            @click="handleSaveClick(true)"
          >
            Sim, Salvar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
